import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ExportButtonProps {
  monitorId?: number;
  className?: string;
  size?: "sm" | "default" | "lg" | "icon";
  variant?: "default" | "outline" | "ghost" | "secondary";
}

export function ExportButton({ monitorId, className, size = "sm", variant = "outline" }: ExportButtonProps) {
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pendingFormat, setPendingFormat] = useState<"excel" | "csv">("excel");
  
  const excelMutation = trpc.export.excel.useMutation();
  const csvMutation = trpc.export.csv.useMutation();
  
  const handleExport = async (format: "excel" | "csv", useDate = false) => {
    try {
      const params = {
        monitorId,
        dateFrom: useDate && dateFrom ? dateFrom : undefined,
        dateTo: useDate && dateTo ? dateTo : undefined,
      };
      
      if (format === "excel") {
        const result = await excelMutation.mutateAsync(params);
        downloadBase64File(result.data, result.filename, result.mimeType);
        toast.success("Excel导出成功");
      } else {
        const result = await csvMutation.mutateAsync(params);
        downloadTextFile(result.data, result.filename, result.mimeType);
        toast.success("CSV导出成功");
      }
      
      setShowDateDialog(false);
    } catch (err: any) {
      toast.error("导出失败: " + (err.message || "未知错误"));
    }
  };
  
  const handleDateExport = (format: "excel" | "csv") => {
    setPendingFormat(format);
    setShowDateDialog(true);
  };
  
  const isExporting = excelMutation.isPending || csvMutation.isPending;
  
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} className={className} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            导出
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs">导出全部</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleExport("excel")}>
            <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
            Excel (.xlsx)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleExport("csv")}>
            <FileText className="h-4 w-4 mr-2 text-blue-600" />
            CSV (.csv)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">按日期范围</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleDateExport("excel")}>
            <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
            Excel (选择日期)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleDateExport("csv")}>
            <FileText className="h-4 w-4 mr-2 text-blue-600" />
            CSV (选择日期)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>选择导出日期范围</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">开始日期</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">结束日期</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDateDialog(false)}>取消</Button>
            <Button onClick={() => handleExport(pendingFormat, true)} disabled={isExporting}>
              {isExporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              导出{pendingFormat === "excel" ? " Excel" : " CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ========== 下载辅助函数 ==========

function downloadBase64File(base64: string, filename: string, mimeType: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadTextFile(text: string, filename: string, mimeType: string) {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
